import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFinance } from '../context/FinanceContext';
import { Plus, Edit2, Trash2, FileText } from 'lucide-react';
import Skeleton from '../components/Skeleton';
import { useToast } from '../context/ToastContext';
import Modal from '../components/Modal';
import Receipt from '../components/Receipt';

export default function MonthDashboard() {
  const { id } = useParams();
  const { currentMonth, fetchMonthDetails, addMultipleBudgets, addMultipleDebts, updateBudget, deleteBudget, updateDebt, deleteDebt, monthLoading, updateMonth } = useFinance();
  
  const [showBudgetForm, setShowBudgetForm] = useState(false);
  const [budgetForms, setBudgetForms] = useState([{ name: '', amount: '', notes: '' }]);
  const [editingBudget, setEditingBudget] = useState(null);
  const [editBudgetForm, setEditBudgetForm] = useState({ name: '', amount: '', notes: '' });

  const [showDebtForm, setShowDebtForm] = useState(false);
  const [debtForms, setDebtForms] = useState([{ name: '', amount: '', notes: '' }]);
  const [editingDebt, setEditingDebt] = useState(null);
  const [editDebtForm, setEditDebtForm] = useState({ name: '', amount: '', notes: '' });

  const [isReceiptOpen, setIsReceiptOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ type: null, id: null });
  
  const [editingRent, setEditingRent] = useState(false);
  const [editRentAmount, setEditRentAmount] = useState('');

  const { addToast } = useToast();

  useEffect(() => {
    fetchMonthDetails(id);
  }, [id, fetchMonthDetails]);

  const handleAddMultipleBudgets = async () => {
    const validForms = budgetForms.filter(f => f.name.trim() !== '' && f.amount !== '');
    const totalNewAmount = validForms.reduce((sum, f) => sum + parseFloat(f.amount || 0), 0);
    
    if (totalNewAmount > currentMonth?.summary?.remaining) {
      addToast(`Cannot add budgets. Exceeds remaining balance by K${totalNewAmount - currentMonth.summary.remaining}.`, 'error');
      return;
    }

    if (validForms.length > 0) {
      await addMultipleBudgets(validForms.map(f => ({ month_id: currentMonth.month.id, ...f })));
      addToast('Budgets added successfully!');
    }
    setShowBudgetForm(false);
    setBudgetForms([{ name: '', amount: '', notes: '' }]);
  };

  const handleAddMultipleDebts = async () => {
    const validForms = debtForms.filter(f => f.name.trim() !== '' && f.amount !== '');
    const totalNewAmount = validForms.reduce((sum, f) => sum + parseFloat(f.amount || 0), 0);
    
    if (totalNewAmount > currentMonth?.summary?.remaining) {
      addToast(`Cannot add debts. Exceeds remaining balance by K${totalNewAmount - currentMonth.summary.remaining}.`, 'error');
      return;
    }

    if (validForms.length > 0) {
      await addMultipleDebts(validForms.map(f => ({ month_id: currentMonth.month.id, ...f })));
      addToast('Debts added successfully!');
    }
    setShowDebtForm(false);
    setDebtForms([{ name: '', amount: '', notes: '' }]);
  };

  const updateBudgetForm = (index, field, value) => {
    const newForms = [...budgetForms];
    newForms[index][field] = value;
    setBudgetForms(newForms);
  };

  const updateDebtForm = (index, field, value) => {
    const newForms = [...debtForms];
    newForms[index][field] = value;
    setDebtForms(newForms);
  };

  const handleEditBudgetClick = (b) => {
    setEditingBudget(b.id);
    setEditBudgetForm({ name: b.name, amount: b.amount, notes: b.notes });
  };
  
  const handleSaveBudgetEdit = async (id) => {
    const oldBudget = currentMonth.budgets.find(b => b.id === id);
    const difference = parseFloat(editBudgetForm.amount || 0) - parseFloat(oldBudget.amount || 0);
    if (difference > currentMonth.summary.remaining) {
      addToast(`Cannot update budget. Exceeds remaining balance by K${difference - currentMonth.summary.remaining}.`, 'error');
      return;
    }
    await updateBudget(id, editBudgetForm);
    setEditingBudget(null);
    addToast('Budget updated successfully!');
  };
  
  const handleEditRentClick = () => {
    setEditingRent(true);
    setEditRentAmount(currentMonth.month.rent_amount);
  };
  
  const handleSaveRentEdit = async () => {
    const newRent = parseFloat(editRentAmount || 0);
    const oldRent = parseFloat(currentMonth.month.rent_amount || 0);
    const difference = newRent - oldRent;
    if (difference > currentMonth.summary.remaining) {
      addToast(`Cannot update rent. Exceeds remaining balance by K${difference - currentMonth.summary.remaining}.`, 'error');
      return;
    }
    await updateMonth(currentMonth.month.id, { rent_amount: newRent });
    setEditingRent(false);
    addToast('Rent updated successfully!');
  };
  
  const handleDeleteBudget = (id) => {
    setConfirmDelete({ type: 'budget', id });
  };

  const handleEditDebtClick = (d) => {
    setEditingDebt(d.id);
    setEditDebtForm({ name: d.name, amount: d.amount, notes: d.notes, status: d.status });
  };
  
  const handleSaveDebtEdit = async (id) => {
    const oldDebt = currentMonth.debts.find(d => d.id === id);
    const difference = parseFloat(editDebtForm.amount || 0) - parseFloat(oldDebt.amount || 0);
    if (difference > currentMonth.summary.remaining) {
      addToast(`Cannot update debt. Exceeds remaining balance by K${difference - currentMonth.summary.remaining}.`, 'error');
      return;
    }
    await updateDebt(id, editDebtForm);
    setEditingDebt(null);
    addToast('Debt updated successfully!');
  };
  
  const handleDeleteDebt = (id) => {
    setConfirmDelete({ type: 'debt', id });
  };

  const handleConfirmDelete = async () => {
    if (confirmDelete.type === 'budget') {
      await deleteBudget(confirmDelete.id);
      addToast('Budget deleted successfully!', 'success');
    } else if (confirmDelete.type === 'debt') {
      await deleteDebt(confirmDelete.id);
      addToast('Debt deleted successfully!', 'success');
    }
    setConfirmDelete({ type: null, id: null });
  };

  if (monthLoading || !currentMonth) {
    return (
      <div className="flex flex-col lg:flex-row gap-8 pb-12 items-start">
        <div className="flex-1 space-y-8 w-full">
          <header className="flex justify-between items-end border-b border-gray-200 pb-4">
            <Skeleton className="h-10 w-64" />
            <Skeleton className="h-8 w-24 rounded-md" />
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <section>
              <div className="flex justify-between items-center mb-4">
                <Skeleton className="h-7 w-24" />
                <Skeleton className="h-5 w-16" />
              </div>
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white p-4 rounded border border-gray-200 h-20 flex justify-between items-center">
                    <div>
                      <Skeleton className="h-5 w-32 mb-2" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                    <Skeleton className="h-6 w-20" />
                  </div>
                ))}
              </div>
            </section>
            <section>
              <div className="flex justify-between items-center mb-4">
                <Skeleton className="h-7 w-24" />
                <Skeleton className="h-5 w-16" />
              </div>
              <div className="space-y-3">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-white p-4 rounded border border-gray-200 h-20 flex justify-between items-center">
                    <div>
                      <Skeleton className="h-5 w-32 mb-2" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Skeleton className="h-6 w-20" />
                      <Skeleton className="h-4 w-12 rounded-full" />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        </div>
        
        <div className="hidden lg:block w-80 shrink-0 sticky top-6">
          <div className="receipt-edges w-full max-w-sm mx-auto p-6 font-mono text-gray-800 text-sm mt-3 mb-3 h-96 flex flex-col items-center justify-center">
             <Skeleton className="h-8 w-32 mb-4" />
             <Skeleton className="h-4 w-48 mb-8" />
             <Skeleton className="h-4 w-full mb-2" />
             <Skeleton className="h-4 w-full mb-2" />
             <Skeleton className="h-4 w-full" />
          </div>
        </div>
      </div>
    );
  }

  const { month, summary, budgets, debts } = currentMonth;
  const monthName = new Date(0, month.month - 1).toLocaleString('default', { month: 'long' });

  return (
    <div className="flex flex-col lg:flex-row gap-8 pb-12 items-start">
      
      {/* Main Content Area */}
      <div className="flex-1 space-y-8 w-full">
        <header className="flex flex-col sm:flex-row justify-between items-start sm:items-end border-b border-gray-200 pb-4 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{monthName} {month.year}</h1>
            <span className={`px-3 py-1 rounded-full text-xs font-medium inline-block mt-2 ${month.status === 'Active' ? 'bg-green-500 text-white' : 'bg-gray-500 text-white'}`}>
              {month.status}
            </span>
          </div>
          <button 
            onClick={() => setIsReceiptOpen(true)}
            className="lg:hidden flex items-center gap-2 bg-gray-800 text-white px-4 py-2 rounded-md hover:bg-gray-900 active:scale-95 transition-all text-sm font-medium"
          >
            <FileText className="w-4 h-4" /> View Receipt
          </button>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Budgets */}
          <section>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-700">Budgets</h2>
              <button onClick={() => setShowBudgetForm(!showBudgetForm)} className="flex items-center gap-1 text-sm text-blue-600 font-medium hover:text-blue-700 active:scale-95 transition-transform">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            
            {showBudgetForm && (
              <div className="bg-gray-50 p-4 rounded-3xl mb-4 border border-gray-200">
                {budgetForms.map((form, index) => (
                  <div key={index} className="grid grid-cols-1 gap-2 mb-3 relative group">
                    <input placeholder="Budget Name (e.g. Home Food)" value={form.name} onChange={e => updateBudgetForm(index, 'name', e.target.value)} className="border p-2 rounded text-sm w-full" />
                    <div className="flex gap-2">
                      <input placeholder="Amount" type="number" value={form.amount} onChange={e => updateBudgetForm(index, 'amount', e.target.value)} className="border p-2 rounded text-sm w-1/3" />
                      <input placeholder="Notes" value={form.notes} onChange={e => updateBudgetForm(index, 'notes', e.target.value)} className="border p-2 rounded text-sm flex-1" />
                    </div>
                    {budgetForms.length > 1 && (
                      <button onClick={() => setBudgetForms(budgetForms.filter((_, i) => i !== index))} className="absolute right-2 top-2 text-red-500 hover:text-red-700 bg-white rounded-full" title="Remove line">
                        &times;
                      </button>
                    )}
                  </div>
                ))}
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                  <button onClick={() => setBudgetForms([...budgetForms, {name: '', amount: '', notes: ''}])} className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add line
                  </button>
                  <div className="flex gap-2">
                    <button onClick={() => { setShowBudgetForm(false); setBudgetForms([{name: '', amount: '', notes: ''}]); }} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 active:scale-95 transition-all rounded font-medium text-sm">Cancel</button>
                    <button onClick={handleAddMultipleBudgets} className="bg-blue-600 text-white px-4 py-1.5 rounded font-medium hover:bg-blue-700 active:scale-95 transition-all text-sm shadow-sm">Save All</button>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {month.rent_amount !== undefined && (
                <div className="bg-white p-4 rounded-3xl border border-gray-200 group">
                  {editingRent ? (
                    <div className="space-y-2">
                      <h3 className="font-medium text-gray-800">Rent (Fixed Commitment)</h3>
                      <div className="flex gap-2">
                        <input 
                          type="number" 
                          value={editRentAmount} 
                          onChange={e => setEditRentAmount(e.target.value)} 
                          className="border p-1.5 rounded text-sm w-1/3"
                          placeholder="Amount"
                        />
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <button onClick={() => setEditingRent(false)} className="bg-gray-200 text-gray-800 px-3 py-1.5 rounded text-xs font-medium">Cancel</button>
                        <button onClick={handleSaveRentEdit} className="bg-green-600 text-white px-3 py-1.5 rounded text-xs font-medium">Save</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div className="overflow-hidden pr-4">
                        <h3 className="font-medium text-gray-800 truncate">Rent</h3>
                        <p className="text-sm text-gray-500 truncate">Fixed Commitment</p>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="font-semibold text-gray-700">K{month.rent_amount}</span>
                        <div className="opacity-0 group-hover:opacity-100 flex gap-2 transition-opacity">
                          <button onClick={handleEditRentClick} className="text-gray-400 hover:text-blue-600"><Edit2 className="w-4 h-4"/></button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {budgets.map(b => (
                <div key={b.id} className="bg-white p-4 rounded-3xl border border-gray-200 group">
                  {editingBudget === b.id ? (
                    <div className="space-y-2">
                      <input value={editBudgetForm.name} onChange={e => setEditBudgetForm({...editBudgetForm, name: e.target.value})} className="border p-1.5 rounded text-sm w-full"/>
                      <div className="flex gap-2">
                        <input type="number" value={editBudgetForm.amount} onChange={e => setEditBudgetForm({...editBudgetForm, amount: e.target.value})} className="border p-1.5 rounded text-sm w-1/3"/>
                        <input value={editBudgetForm.notes || ''} onChange={e => setEditBudgetForm({...editBudgetForm, notes: e.target.value})} className="border p-1.5 rounded text-sm flex-1" placeholder="Notes"/>
                      </div>
                      <div className="flex justify-end gap-2 pt-2">
                        <button onClick={() => setEditingBudget(null)} className="bg-gray-200 text-gray-800 px-3 py-1.5 rounded text-xs font-medium">Cancel</button>
                        <button onClick={() => handleSaveBudgetEdit(b.id)} className="bg-green-600 text-white px-3 py-1.5 rounded text-xs font-medium">Save</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div className="overflow-hidden pr-4">
                        <h3 className="font-medium text-gray-800 truncate">{b.name}</h3>
                        {b.notes && <p className="text-sm text-gray-500 truncate">{b.notes}</p>}
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <span className="font-semibold text-gray-700">K{b.amount}</span>
                        <div className="opacity-0 group-hover:opacity-100 flex gap-2 transition-opacity">
                          <button onClick={() => handleEditBudgetClick(b)} className="text-gray-400 hover:text-blue-600"><Edit2 className="w-4 h-4"/></button>
                          <button onClick={() => handleDeleteBudget(b.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {budgets.length === 0 && Number(month.rent_amount) === 0 && (
                <p className="text-gray-500 text-sm">No budgets created yet.</p>
              )}
            </div>
          </section>

          {/* Debts */}
          <section>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-700">Debts</h2>
              <button onClick={() => setShowDebtForm(!showDebtForm)} className="flex items-center gap-1 text-sm text-blue-600 font-medium hover:text-blue-700 active:scale-95 transition-transform">
                <Plus className="w-4 h-4" /> Add
              </button>
            </div>
            
            {showDebtForm && (
              <div className="bg-gray-50 p-4 rounded-3xl mb-4 border border-gray-200">
                {debtForms.map((form, index) => (
                  <div key={index} className="grid grid-cols-1 gap-2 mb-3 relative group">
                    <input placeholder="Debt Name" value={form.name} onChange={e => updateDebtForm(index, 'name', e.target.value)} className="border p-2 rounded text-sm w-full" />
                    <div className="flex gap-2">
                      <input placeholder="Amount" type="number" value={form.amount} onChange={e => updateDebtForm(index, 'amount', e.target.value)} className="border p-2 rounded text-sm w-1/3" />
                      <input placeholder="Notes" value={form.notes} onChange={e => updateDebtForm(index, 'notes', e.target.value)} className="border p-2 rounded text-sm flex-1" />
                    </div>
                    {debtForms.length > 1 && (
                      <button onClick={() => setDebtForms(debtForms.filter((_, i) => i !== index))} className="absolute right-2 top-2 text-red-500 hover:text-red-700 bg-white rounded-full" title="Remove line">
                        &times;
                      </button>
                    )}
                  </div>
                ))}
                <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                  <button onClick={() => setDebtForms([...debtForms, {name: '', amount: '', notes: ''}])} className="text-sm font-medium text-blue-600 hover:text-blue-800 flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Add line
                  </button>
                  <div className="flex gap-2">
                    <button onClick={() => { setShowDebtForm(false); setDebtForms([{name: '', amount: '', notes: ''}]); }} className="px-3 py-1.5 text-gray-600 hover:bg-gray-100 active:scale-95 transition-all rounded font-medium text-sm">Cancel</button>
                    <button onClick={handleAddMultipleDebts} className="bg-blue-600 text-white px-4 py-1.5 rounded font-medium hover:bg-blue-700 active:scale-95 transition-all text-sm shadow-sm">Save All</button>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {debts.map(d => (
                <div key={d.id} className={`bg-white p-4 rounded-3xl border group ${d.status === 'Paid' ? 'border-green-400 bg-green-200' : 'border-red-200'}`}>
                  {editingDebt === d.id ? (
                    <div className="space-y-2">
                      <input value={editDebtForm.name} onChange={e => setEditDebtForm({...editDebtForm, name: e.target.value})} className="border p-1.5 rounded text-sm w-full"/>
                      <div className="flex gap-2">
                        <input type="number" value={editDebtForm.amount} onChange={e => setEditDebtForm({...editDebtForm, amount: e.target.value})} className="border p-1.5 rounded text-sm w-1/3"/>
                        <select value={editDebtForm.status} onChange={e => setEditDebtForm({...editDebtForm, status: e.target.value})} className="border p-1.5 rounded text-sm flex-1">
                          <option value="Unpaid">Unpaid</option>
                          <option value="Paid">Paid</option>
                        </select>
                      </div>
                      <input value={editDebtForm.notes || ''} onChange={e => setEditDebtForm({...editDebtForm, notes: e.target.value})} className="border p-1.5 rounded text-sm w-full" placeholder="Notes"/>
                      <div className="flex justify-end gap-2 pt-2">
                        <button onClick={() => setEditingDebt(null)} className="bg-gray-200 text-gray-800 px-3 py-1.5 rounded text-xs font-medium">Cancel</button>
                        <button onClick={() => handleSaveDebtEdit(d.id)} className="bg-green-600 text-white px-3 py-1.5 rounded text-xs font-medium">Save</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center">
                      <div className="overflow-hidden pr-4">
                        <h3 className="font-medium text-gray-800 truncate">{d.name}</h3>
                        {d.notes && <p className="text-sm text-gray-500 truncate">{d.notes}</p>}
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-right">
                          <span className={`font-semibold block ${d.status === 'Paid' ? 'text-green-600 line-through' : 'text-red-600'}`}>K{d.amount}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full mt-1 inline-block ${d.status === 'Paid' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>{d.status}</span>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 flex flex-col gap-2 transition-opacity ml-2">
                          <button onClick={() => handleEditDebtClick(d)} className="text-gray-400 hover:text-blue-600"><Edit2 className="w-4 h-4"/></button>
                          <button onClick={() => handleDeleteDebt(d.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4"/></button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {debts.length === 0 && (
                <p className="text-gray-500 text-sm">No debts for this month.</p>
              )}
            </div>
          </section>
        </div>
      </div>

      {/* Desktop Receipt */}
      <div className="hidden lg:block w-80 shrink-0 sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto no-scrollbar pb-4">
        <Receipt month={month} summary={summary} budgets={budgets} debts={debts} />
      </div>

      {/* Mobile Receipt Modal */}
      <Modal isOpen={isReceiptOpen} onClose={() => setIsReceiptOpen(false)}>
        <Receipt month={month} summary={summary} budgets={budgets} debts={debts} />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal 
        isOpen={confirmDelete.type !== null} 
        onClose={() => setConfirmDelete({ type: null, id: null })}
        title={`Delete ${confirmDelete.type === 'budget' ? 'Budget' : 'Debt'}`}
      >
        <div className="text-gray-600 mb-6">
          Are you sure you want to delete this {confirmDelete.type}? This action cannot be undone.
        </div>
        <div className="flex justify-end gap-3">
          <button 
            onClick={() => setConfirmDelete({ type: null, id: null })}
            className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded font-medium transition-colors"
          >
            Cancel
          </button>
          <button 
            onClick={handleConfirmDelete}
            className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded font-medium transition-colors shadow-sm"
          >
            Delete
          </button>
        </div>
      </Modal>
    </div>
  );
}
